<template>
  <div class="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center min-h-[400px]">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#157813] mx-auto mb-4"></div>
        <p class="text-lg text-gray-600">Loading dashboard data...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="flex items-center justify-center min-h-[400px]">
      <div class="text-center bg-white rounded-xl shadow-lg p-8">
        <div class="text-red-500 mb-4">
          <svg class="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
          </svg>
        </div>
        <h2 class="text-xl font-bold text-gray-900 mb-2">Error Loading Data</h2>
        <p class="text-gray-600 mb-4">{{ error }}</p>
        <button 
          @click="$router.go(0)" 
          class="bg-[#157813] text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
        >
          Retry
        </button>
      </div>
    </div>

    <!-- Main Dashboard Content -->
    <div v-else>
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-4xl font-bold text-gray-900 mb-2">Analytics Dashboard</h1>
        <p class="text-lg text-gray-600">
          {{ demoData.overall_stats.time_period }}
        </p>
      </div>

    <!-- Overall Stats Cards -->
    <div
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8"
    >
      <div
        class="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#157813] hover:shadow-xl transition-shadow duration-300"
      >
        <div class="flex items-center justify-between">
          <div>
            <p
              class="text-sm font-medium text-gray-600 uppercase tracking-wide"
            >
              Client Queries
            </p>
            <p class="text-3xl font-bold text-gray-900">
              {{
                formatNumber(
                  demoData.overall_stats.number_of_queries_from_the_client
                )
              }}
            </p>
          </div>
          <div class="bg-[#157813]/10 p-3 rounded-full">
            <LucideMessageSquare class="w-8 h-8 text-[#157813]" />
          </div>
        </div>
      </div>

      <div
        class="bg-white rounded-xl shadow-lg p-6 border-l-4 border-gray-600 hover:shadow-xl transition-shadow duration-300"
      >
        <div class="flex items-center justify-between">
          <div>
            <p
              class="text-sm font-medium text-gray-600 uppercase tracking-wide"
            >
              Unique Queries
            </p>
            <p class="text-3xl font-bold text-gray-900">
              {{
                formatNumber(
                  demoData.overall_stats.unique_queries_from_the_client
                )
              }}
            </p>
          </div>
          <div class="bg-gray-600/10 p-3 rounded-full">
            <LucideFilter class="w-8 h-8 text-gray-600" />
          </div>
        </div>
      </div>

      <div
        class="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-600 hover:shadow-xl transition-shadow duration-300"
      >
        <div class="flex items-center justify-between">
          <div>
            <p
              class="text-sm font-medium text-gray-600 uppercase tracking-wide"
            >
              Decomposed Queries
            </p>
            <p class="text-3xl font-bold text-gray-900">
              {{
                formatNumber(
                  demoData.overall_stats.number_of_decomposed_queries
                )
              }}
            </p>
          </div>
          <div class="bg-green-600/10 p-3 rounded-full">
            <LucideGitBranch class="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>

      <div
        class="bg-white rounded-xl shadow-lg p-6 border-l-4 border-gray-700 hover:shadow-xl transition-shadow duration-300"
      >
        <div class="flex items-center justify-between">
          <div>
            <p
              class="text-sm font-medium text-gray-600 uppercase tracking-wide"
            >
              Unique Neurons
            </p>
            <p class="text-3xl font-bold text-gray-900">
              {{ formatNumber(demoData.overall_stats.unique_neurons) }}
            </p>
          </div>
          <div class="bg-gray-700/10 p-3 rounded-full">
            <LucideBrain class="w-8 h-8 text-gray-700" />
          </div>
        </div>
      </div>

      <div
        class="bg-white rounded-xl shadow-lg p-6 border-l-4 border-gray-800 hover:shadow-xl transition-shadow duration-300"
      >
        <div class="flex items-center justify-between">
          <div>
            <p
              class="text-sm font-medium text-gray-600 uppercase tracking-wide"
            >
              Unique Users
            </p>
            <p class="text-3xl font-bold text-gray-900">
              {{ formatNumber(demoData.overall_stats.unique_users) }}
            </p>
          </div>
          <div class="bg-gray-800/10 p-3 rounded-full">
            <LucideUsers class="w-8 h-8 text-gray-800" />
          </div>
        </div>
      </div>
    </div>

    <!-- Enrichment Stats -->
    <div v-if="hasEnrichmentStats" class="bg-white rounded-xl shadow-lg p-6 mb-8">
      <h2 class="text-2xl font-bold text-gray-900 mb-6 flex items-center">
        <LucideBarChart3 class="w-6 h-6 text-[#157813] mr-2" />
        Enrichment Statistics
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div v-if="demoData.enrichment_stats.medication_enrichments > 0" class="text-center">
          <div
            class="bg-gradient-to-br from-[#157813] to-green-700 text-white rounded-lg p-4 mb-2"
          >
            <p class="text-2xl font-bold">
              {{
                formatNumber(demoData.enrichment_stats.medication_enrichments)
              }}
            </p>
          </div>
          <p class="text-sm font-medium text-gray-600">
            Medication Enrichments
          </p>
        </div>
        <div v-if="demoData.enrichment_stats.indication_enrichments > 0" class="text-center">
          <div
            class="bg-gradient-to-br from-gray-700 to-gray-800 text-white rounded-lg p-4 mb-2"
          >
            <p class="text-2xl font-bold">
              {{ demoData.enrichment_stats.indication_enrichments }}
            </p>
          </div>
          <p class="text-sm font-medium text-gray-600">
            Indication Enrichments
          </p>
        </div>
        <div v-if="demoData.enrichment_stats.features > 0" class="text-center">
          <div
            class="bg-gradient-to-br from-green-600 to-green-700 text-white rounded-lg p-4 mb-2"
          >
            <p class="text-2xl font-bold">
              {{ formatNumber(demoData.enrichment_stats.features) }}
            </p>
          </div>
          <p class="text-sm font-medium text-gray-600">Features</p>
        </div>
      </div>
    </div>

    <!-- Breakdown Charts -->
    <div v-if="hasBreakdownSections" class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <!-- Analytics Type Breakdown -->
      <div v-if="demoData.enrichment_breakdowns.analytics_type.length > 0" class="bg-white rounded-xl shadow-lg p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <LucidePieChart class="w-5 h-5 text-[#157813] mr-2" />
          Analytics Type Distribution
        </h3>
        <div class="space-y-3">
          <div
            v-for="(item, index) in demoData.enrichment_breakdowns
              .analytics_type"
            :key="index"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div class="flex items-center">
              <div
                :class="`w-4 h-4 rounded-full mr-3 ${getColorClass(
                  index,
                  'green'
                )}`"
              ></div>
              <span class="font-medium text-gray-700 capitalize">{{
                formatLabel(item.value)
              }}</span>
            </div>
            <span class="font-bold text-gray-900">{{
              formatNumber(item.cnt)
            }}</span>
          </div>
        </div>
      </div>

      <!-- Business Rules Breakdown -->
      <div v-if="demoData.enrichment_breakdowns.business_rules.length > 0" class="bg-white rounded-xl shadow-lg p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <LucideSettings class="w-5 h-5 text-gray-600 mr-2" />
          Business Rules Distribution
        </h3>
        <div class="space-y-3">
          <div
            v-for="(item, index) in demoData.enrichment_breakdowns
              .business_rules"
            :key="index"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div class="flex items-center">
              <div
                :class="`w-4 h-4 rounded-full mr-3 ${getColorClass(
                  index,
                  'gray'
                )}`"
              ></div>
              <span class="font-medium text-gray-700 capitalize">{{
                formatLabel(item.value)
              }}</span>
            </div>
            <span class="font-bold text-gray-900">{{
              formatNumber(item.cnt)
            }}</span>
          </div>
        </div>
      </div>

      <!-- Features Breakdown -->
      <div v-if="demoData.enrichment_breakdowns.features.length > 0" class="bg-white rounded-xl shadow-lg p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <LucideDatabase class="w-5 h-5 text-green-600 mr-2" />
          Top Features
        </h3>
        <div class="space-y-3">
          <div
            v-for="(
              item, index
            ) in demoData.enrichment_breakdowns.features.slice(0, 6)"
            :key="index"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div class="flex items-center">
              <div
                :class="`w-4 h-4 rounded-full mr-3 ${getColorClass(
                  index,
                  'green'
                )}`"
              ></div>
              <span class="font-medium text-gray-700 capitalize">{{
                formatLabel(item.value)
              }}</span>
            </div>
            <span class="font-bold text-gray-900">{{
              formatNumber(item.cnt)
            }}</span>
          </div>
        </div>
      </div>

      <!-- Indication Enrichments Breakdown -->
      <div v-if="demoData.enrichment_breakdowns.indication_enrichments.length > 0" class="bg-white rounded-xl shadow-lg p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <LucideActivity class="w-5 h-5 text-gray-700 mr-2" />
          Medical Indications
        </h3>
        <div class="space-y-3">
          <div
            v-for="(item, index) in demoData.enrichment_breakdowns
              .indication_enrichments"
            :key="index"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div class="flex items-center">
              <div
                :class="`w-4 h-4 rounded-full mr-3 ${getColorClass(
                  index,
                  'dark'
                )}`"
              ></div>
              <span class="font-medium text-gray-700 capitalize">{{
                formatLabel(item.value)
              }}</span>
            </div>
            <span class="font-bold text-gray-900">{{
              formatNumber(item.cnt)
            }}</span>
          </div>
        </div>
      </div>

      <!-- Medication Enrichments Breakdown -->
      <div v-if="demoData.enrichment_breakdowns.medication_enrichments.length > 0" class="bg-white rounded-xl shadow-lg p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <LucidePill class="w-5 h-5 text-[#157813] mr-2" />
          Top Medications
        </h3>
        <div class="space-y-3">
          <div
            v-for="(item, index) in demoData.enrichment_breakdowns
              .medication_enrichments"
            :key="index"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div class="flex items-center">
              <div
                :class="`w-4 h-4 rounded-full mr-3 ${getColorClass(
                  index,
                  'green'
                )}`"
              ></div>
              <span class="font-medium text-gray-700 capitalize">{{
                formatLabel(item.value)
              }}</span>
            </div>
            <span class="font-bold text-gray-900">{{
              formatNumber(item.cnt)
            }}</span>
          </div>
        </div>
      </div>

      <!-- Project Steps Breakdown -->
      <div v-if="demoData.enrichment_breakdowns.project_step.length > 0" class="bg-white rounded-xl shadow-lg p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <LucideGitCommit class="w-5 h-5 text-gray-800 mr-2" />
          Project Steps
        </h3>
        <div class="space-y-3">
          <div
            v-for="(item, index) in demoData.enrichment_breakdowns.project_step"
            :key="index"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div class="flex items-center">
              <div
                :class="`w-4 h-4 rounded-full mr-3 ${getColorClass(
                  index,
                  'dark'
                )}`"
              ></div>
              <span class="font-medium text-gray-700 capitalize">{{
                formatLabel(item.value)
              }}</span>
            </div>
            <span class="font-bold text-gray-900">{{
              formatNumber(item.cnt)
            }}</span>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script lang="ts">
import { calculateSummaryData, type SummaryData } from "@/models/Query";
import {
    LucideActivity,
    LucideBarChart3,
    LucideBrain,
    LucideDatabase,
    LucideFileText,
    LucideFilter,
    LucideGitBranch,
    LucideGitCommit,
    LucideMessageSquare,
    LucidePieChart,
    LucidePill,
    LucideSettings,
    LucideUsers,
} from "lucide-vue-next";
import { computed, defineComponent, onMounted, ref } from "vue";

// Interfaces are now imported from Query.ts

export default defineComponent({
  name: "Summary",
  components: {
    LucideMessageSquare,
    LucideFilter,
    LucideGitBranch,
    LucideBrain,
    LucideFileText,
    LucideUsers,
    LucideBarChart3,
    LucidePieChart,
    LucideSettings,
    LucideDatabase,
    LucideActivity,
    LucidePill,
    LucideGitCommit,
  },
  setup() {
    const summaryData = ref<SummaryData | null>(null);
    const isLoading = ref(true);
    const error = ref<string | null>(null);

    // Load summary data on component mount
    onMounted(async () => {
      try {
        isLoading.value = true;
        error.value = null;
        summaryData.value = await calculateSummaryData();
      } catch (err) {
        console.error('Failed to load summary data:', err);
        error.value = 'Failed to load dashboard data. Please try again later.';
      } finally {
        isLoading.value = false;
      }
    });

    // Fallback demo data for when real data is not available
    const fallbackData: SummaryData = {
      overall_stats: {
        number_of_queries_from_the_client: 0,
        unique_queries_from_the_client: 0,
        number_of_decomposed_queries: 0,
        unique_neuronIDs: 0,
        unique_neuron_txt: 0,
        unique_neurons: 0,
        unique_users: 0,
        time_period: "No data available",
      },
      enrichment_stats: {
        analytics_type: 0,
        business_rules: 0,
        features: 0,
        indication_enrichments: 0,
        medication_enrichments: 0,
        project_steps: 0,
      },
      enrichment_breakdowns: {
        analytics_type: [],
        business_rules: [],
        features: [],
        indication_enrichments: [],
        medication_enrichments: [],
        project_step: [],
      },
    };

    // Computed property to get data with fallback
    const demoData = computed(() => summaryData.value || fallbackData);

    // Computed property to check if there are any enrichment stats to show
    const hasEnrichmentStats = computed(() => {
      const stats = demoData.value.enrichment_stats;
      return stats.medication_enrichments > 0 || 
             stats.indication_enrichments > 0 || 
             stats.features > 0;
    });

    // Computed property to check if there are any breakdown sections to show
    const hasBreakdownSections = computed(() => {
      const breakdowns = demoData.value.enrichment_breakdowns;
      return breakdowns.analytics_type.length > 0 ||
             breakdowns.business_rules.length > 0 ||
             breakdowns.features.length > 0 ||
             breakdowns.indication_enrichments.length > 0 ||
             breakdowns.medication_enrichments.length > 0 ||
             breakdowns.project_step.length > 0;
    });

    const formatNumber = (num: number): string => {
      return num.toLocaleString();
    };

    const formatLabel = (label: string): string => {
      return label.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    };

    const getColorClass = (
      index: number,
      colorScheme: string = "green"
    ): string => {
      const colorSchemes = {
        green: [
          "bg-[#157813]",
          "bg-green-600",
          "bg-green-700",
          "bg-green-500",
          "bg-green-800",
        ],
        gray: [
          "bg-gray-600",
          "bg-gray-700",
          "bg-gray-500",
          "bg-gray-800",
          "bg-gray-900",
        ],
        dark: [
          "bg-gray-700",
          "bg-gray-800",
          "bg-gray-900",
          "bg-black",
          "bg-gray-600",
        ],
      };

      const colors =
        colorSchemes[colorScheme as keyof typeof colorSchemes] ||
        colorSchemes.green;
      return colors[index % colors.length];
    };

    return {
      demoData,
      isLoading,
      error,
      hasEnrichmentStats,
      hasBreakdownSections,
      formatNumber,
      formatLabel,
      getColorClass,
    };
  },
});
</script>

<style scoped>
/* Additional custom styles if needed */
.hover\:shadow-xl:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}
</style>
